/**
 * Currency Symbol Mapping Service
 *
 * Maps ISO 4217 currency codes to their respective symbols
 */

const CURRENCY_SYMBOLS = {
  // North America
  'USD': '$',
  'CAD': '$',
  'MXN': '$',

  // Europe
  'EUR': '€',
  'GBP': '£',
  'CHF': 'CHF',
  'SEK': 'kr',
  'NOK': 'kr',
  'DKK': 'kr',
  'PLN': 'zł',
  'CZK': 'Kč',
  'HUF': 'Ft',
  'RON': 'lei',
  'BGN': 'лв',
  'HRK': 'kn',
  'RUB': '₽',
  'TRY': '₺',

  // Asia
  'CNY': '¥',
  'JPY': '¥',
  'KRW': '₩',
  'INR': '₹',
  'THB': '฿',
  'VND': '₫',
  'PHP': '₱',
  'IDR': 'Rp',
  'MYR': 'RM',
  'SGD': '$',
  'HKD': '$',
  'TWD': '$',
  'AED': 'د.إ',
  'SAR': 'ر.س',
  'ILS': '₪',

  // Oceania
  'AUD': '$',
  'NZD': '$',

  // South America
  'BRL': 'R$',
  'ARS': '$',
  'CLP': '$',
  'COP': '$',
  'PEN': 'S/',

  // Africa
  'ZAR': 'R',
  'EGP': '£',
  'NGN': '₦',
  'KES': 'KSh',
};

/**
 * Get currency symbol for a given currency code.
 * Uses Intl.NumberFormat to produce the locale-aware narrow symbol (e.g. "CA$" for CAD,
 * "$" for USD, "£" for GBP). Falls back to the static map, then bare "$".
 * @param {string} currencyCode - ISO 4217 currency code (e.g., 'USD', 'CAD', 'EUR')
 * @returns {string} Currency symbol (e.g., 'CA$', '$', '€')
 */
export function getCurrencySymbol(currencyCode) {
  if (!currencyCode) return '$'
  const code = currencyCode.toUpperCase()
  return CURRENCY_SYMBOLS[code] || '$'
}

/**
 * Format a number as currency with the appropriate symbol
 * @param {number} value - The numeric value to format
 * @param {string} currencyCode - ISO 4217 currency code
 * @param {boolean} showDecimals - Whether to show decimal places
 * @param {boolean} showZero - Whether to show zero values
 * @returns {string|null} Formatted currency string
 */
export function formatCurrency(value, currencyCode = 'USD', showDecimals = true, showZero = false) {
  if (!value && !(showZero && value === 0)) {
    return null;
  }

  const symbol = getCurrencySymbol(currencyCode);
  const formattedValue = showDecimals ? value.toFixed(2) : value;

  return `${symbol}${formattedValue}`;
}

/**
 * Format a discount cap (sent by the backend in cents) using the store's currency.
 * Currencies without minor units (JPY, KRW, VND...) render without decimals.
 * @param {number} cents - Cap amount in cents, as maxDiscountAmount from the API
 * @param {string} currencyCode - ISO 4217 currency code
 * @returns {string|null} Formatted cap, or null when there is no cap
 */
export function formatDiscountCap(cents, currencyCode = 'USD') {
  if (cents === null || cents === undefined || cents <= 0) {
    return null;
  }
  const { usesDecimals } = getCurrencyInfo(currencyCode);
  return formatCurrency(cents / 100, currencyCode, usesDecimals, true);
}

/**
 * Get currency info including symbol and decimal requirements
 * @param {string} currencyCode - ISO 4217 currency code
 * @returns {object} Currency information
 */
export function getCurrencyInfo(currencyCode) {
  const symbol = getCurrencySymbol(currencyCode);

  // Some currencies don't use decimal places
  const noDecimalCurrencies = ['JPY', 'KRW', 'VND', 'CLP', 'KMF', 'DJF', 'GNF', 'ISK', 'PYG', 'RWF', 'UGX', 'VUV', 'XAF', 'XOF', 'XPF'];
  const usesDecimals = !noDecimalCurrencies.includes(currencyCode ? currencyCode.toUpperCase() : 'USD');

  return {
    code: currencyCode,
    symbol,
    usesDecimals
  };
}
